// filepath: apps/web/src/features/lists/EntityListPage.tsx
//
// FR-UI-LIST-1 — page composition root for the list view.
//
// Owns the 5-state contract (FR-UI-CORE-10) for the list surface:
//   - loading               → SkeletonLayout
//   - fetch-failure         → FetchError (500 / network)
//   - permission-denied     → PermissionDenied (HTTP 403 / 404 on 'active')
//   - stale-version         → StaleVersionError (HTTP 410 / 404 on pinned)
//   - rate-limit            → RateLimitBackpressure (HTTP 429 + Retry-After)
//   - success               → EntityListTable + FilterControls + PaginationBar
//
// SECURITY LOAD-BEARING NOTE: when the server returns HTTP 200 with
// `records: []` (the FR-QRY-3 AC-1 no-CanRead path), the page MUST
// render the empty-state chrome inside the table — NOT a
// permission-denied banner. That would leak the entity type's
// existence (the impact report's "FR-UI-CORE-10 5-states" finding).
// We default to empty-state chrome here; if a future wire-shape adds
// a distinct `has_can_read` flag, the page can route to
// permission-denied on `has_can_read: false` while preserving the
// current default.

import React, { useCallback, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import { EntityListTable } from "./EntityListTable";
import { FilterControls } from "./FilterControls";
import { PaginationBar } from "./PaginationBar";
import { useEntityList } from "./hooks/useEntityList";
import {
  useEntityListFilter,
  computeAllowlist,
} from "./hooks/useEntityListFilter";
import { useKeysetPagination } from "./hooks/useKeysetPagination";
import { KanbanBoard } from "./kanban/KanbanBoard";
import { TreeView } from "./tree/TreeView";
import { GanttView } from "./gantt/GanttView";
import type { EntityDefinitionWire, EntityListStatus } from "./types";

export interface EntityListPageProps {
  /** Target entity type id. */
  entityType: string;
  /**
   * Resolved entity definition. When undefined, the page fetches
   * via the supplied `definitionFetcher` (the parent route shell
   * already has the definition cached by `useDefinition`).
   */
  definition?: EntityDefinitionWire | null;
  /**
   * Optional explicit definition fetcher. Used when the page is
   * mounted in isolation (tests / demo). In production the route
   * shell passes the resolved definition as a prop.
   */
  definitionFetcher?: (entityType: string) => Promise<EntityDefinitionWire>;
}

/**
 * Classify a TanStack Query result into one of the 5 mandatory
 * renderer states. Mirrors `useDefinition.classifyResult` but
 * scoped to the list-view surface.
 *
 * - 401 / 403 → permission-denied
 * - 404 + 'active' → permission-denied (entity type not visible)
 * - 410 Gone → stale-version
 * - 429 → rate-limit
 * - 4xx other → fetch-failure
 * - 5xx / network → fetch-failure
 * - 200 → success
 */
export function classifyListError(err: ApiError): EntityListStatus {
  if (err.status === 401 || err.status === 403) return "permission-denied";
  if (err.status === 429) return "rate-limit";
  if (err.status === 410) return "stale-version";
  if (err.status === 404) return "permission-denied";
  return "fetch-failure";
}

/**
 * EntityListPage — page composition root for the list view.
 *
 * The page owns the cursor state (forward-only keyset). Filter +
 * sort live in the URL via useEntityListFilter. The page passes
 * the cursor through useKeysetPagination to derive the next-page
 * query params.
 */
export function EntityListPage({
  entityType,
  definition,
  definitionFetcher,
}: EntityListPageProps): React.ReactElement {
  const { t } = useTranslation();

  // URL-state hook — owns filter + sort in the URL query string.
  // The allowlist is derived from the definition passed in.
  const filterState = useEntityListFilter({ definition: definition ?? null });

  // Cursor state lives in the page (not URL) — keyset cursors are
  // opaque base64 tokens; persisting them in the URL would clutter
  // the back-button experience (each page is a distinct entry).
  const [cursor, setCursor] = useState<string | null>(null);

  // Reset the cursor when the filter or sort changes (filter/sort
  // invalidates the cursor — the next-page query uses the new
  // filter/sort from the first page, not the prior cursor).
  React.useEffect(() => {
    setCursor(null);
    // We intentionally depend on the JSON-serialised forms so the
    // effect fires on actual value changes, not reference changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityType,
    JSON.stringify(filterState.filter),
    filterState.sort.map((s) => `${s.field}:${s.dir}`).join(","),
  ]);

  // The page-level fetch. The hook is disabled until the
  // definition is resolved (we need the definition only for the
  // allowlist mirror; the records fetch itself doesn't depend on
  // the definition). We always enable the fetch when entityType
  // is non-empty.
  const list = useEntityList({
    entityType,
    filter: filterState.filter,
    sort: filterState.sort,
    cursor,
    enabled: entityType.length > 0,
  });

  // When a definitionFetcher is supplied (test/demo mode), fall back
  // to its result via a local state. In production, the route
  // passes `definition` directly.
  //
  // SECURITY FIX (security-review MAJOR finding): the fetcher
  // invocation is wrapped in try/catch. A server-side HTTP 4xx/5xx
  // on /api/entity-definitions/{entityType} (e.g. FR-AUTH-5 no
  // CanRead at the entity-type level) previously left the page
  // stuck in <SkeletonLayout /> indefinitely. The error is now
  // routed through classifyListError so the page surfaces the
  // permission-denied / stale-version / rate-limit / fetch-failure
  // state instead.
  const [fetchedDefinition, setFetchedDefinition] =
    useState<EntityDefinitionWire | null>(null);
  const [definitionError, setDefinitionError] = useState<ApiError | null>(null);
  // Bumped on retry — listed in the useEffect deps so clicking
  // "retry" on a definition-fetch failure re-attempts the fetch.
  const [definitionRetryToken, setDefinitionRetryToken] = useState(0);
  React.useEffect(() => {
    if (definition !== undefined && definition !== null) return;
    if (definitionFetcher === undefined) return;
    // Reset the error state on (re)entry so a retry re-attempts the
    // fetch from a clean slate (mirrors how TanStack Query clears
    // its error on refetch).
    setDefinitionError(null);
    let cancelled = false;
    void (async () => {
      try {
        const def = await definitionFetcher(entityType);
        if (!cancelled) setFetchedDefinition(def);
      } catch (err) {
        if (cancelled) return;
        // Normalise non-ApiError rejections (e.g. network errors
        // thrown synchronously by the fetch polyfill) into ApiError
        // instances so they flow through classifyListError. Mirrors
        // the network-error wrapping at useEntityList.ts:201-208.
        const apiErr =
          err instanceof ApiError
            ? err
            : new ApiError(
                err instanceof Error ? err.message : "network error",
                0,
                "network_error",
              );
        setDefinitionError(apiErr);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [definition, definitionFetcher, entityType, definitionRetryToken]);
  const effectiveDefinition =
    definition !== undefined && definition !== null
      ? definition
      : fetchedDefinition;

  // useKeysetPagination must be called UNCONDITIONALLY (Rules of
  // Hooks). It returns no-op data when the records envelope is
  // absent (e.g. during loading / error states), so the early-return
  // branches below can safely render the design-system error states
  // without consulting the hook output.
  const pagination = useKeysetPagination({
    filter: filterState.filter,
    sort: filterState.sort,
    cursor,
    nextCursor: list.data?.nextCursor ?? "",
  });

  // handleToggleSort + handleNext must also be unconditional
  // (Rules of Hooks). They're no-ops during loading / error states.
  const handleToggleSort = useCallback(
    (field: string) => {
      filterState.toggleSort(field);
    },
    [filterState],
  );

  const handleNext = useCallback(() => {
    const next = pagination.buildNextPageParams();
    if (next === null) return;
    setCursor(next.cursor);
  }, [pagination]);

  // Loading — definition is still in flight (only when supplied via
  // fetcher) OR the records fetch is in flight on the first render.
  //
  // SECURITY FIX (security-review MAJOR finding): a definitionError
  // short-circuits the loading branch — without this check, a
  // definition-fetch failure would still render the skeleton
  // (definitionLoading is true whenever fetchedDefinition is null).
  const definitionLoading =
    definition === undefined &&
    definitionFetcher !== undefined &&
    fetchedDefinition === null &&
    definitionError === null;
  if (definitionError !== null) {
    return (
      <ListErrorSurface
        status={classifyListError(definitionError)}
        error={definitionError}
        onRetry={() => setDefinitionRetryToken((n) => n + 1)}
      />
    );
  }
  if (definitionLoading || (list.isLoading && cursor === null)) {
    return (
      <main
        data-testid="entity-list-page--loading"
        aria-labelledby="entity-list-title"
        className="entity-list-page entity-list-page--loading"
      >
        <h1
          id="entity-list-title"
          className="entity-list-title visually-hidden"
        >
          {t("lists.title", "Records")}
        </h1>
        <SkeletonLayout />
      </main>
    );
  }

  // Error states (FR-UI-CORE-10). The render shape is identical to
  // the definition-error path — both delegate to ListErrorSurface.
  if (list.error) {
    return (
      <ListErrorSurface
        status={classifyListError(list.error)}
        error={list.error}
        onRetry={() => {
          void list.refetch();
        }}
      />
    );
  }

  if (!list.data || !effectiveDefinition) {
    // Defensive — should be unreachable because the loading path
    // covers `list.isLoading && cursor === null`.
    return (
      <main
        data-testid="entity-list-page--loading"
        aria-labelledby="entity-list-title"
        className="entity-list-page"
      >
        <h1
          id="entity-list-title"
          className="entity-list-title visually-hidden"
        >
          {t("lists.title", "Records")}
        </h1>
        <SkeletonLayout />
      </main>
    );
  }

  // Success path.
  const allowlist = computeAllowlist(effectiveDefinition);

  // FR-UI-VIEW-1 / FR-UI-VIEW-2 — branch on the resolved
  // `view_config.default_view_type`. The kanban window renders when
  // the entity was published with `default_view_type = "kanban"`,
  // the tree window renders when it is "tree"; otherwise we render
  // the existing table surface unchanged. The view_config block is
  // an additive field on `definition_json` (older deployments omit
  // it and the default is "table" — see
  // internal/entity/handlers.go).
  const viewConfig = (
    effectiveDefinition as unknown as {
      definition_json?: {
        view_config?: {
          default_view_type?: string;
          kanban?: unknown;
          tree?: unknown;
        };
      };
    }
  )?.definition_json?.view_config;
  const defaultViewType = viewConfig?.default_view_type;
  const isKanban = defaultViewType === "kanban";
  // FR-UI-VIEW-2 — tree view dispatches when default_view_type is "tree".
  const isTree = defaultViewType === "tree";
  // FR-UI-VIEW-3 — gantt view dispatches when default_view_type is "gantt".
  // Placed BEFORE the tree branch so the dispatch order matches the task
  // instruction (gantt → tree → kanban → table).
  const isGantt = defaultViewType === "gantt";

  return (
    <main
      data-testid="entity-list-page"
      aria-labelledby="entity-list-title"
      className="entity-list-page"
    >
      <h1
        id="entity-list-title"
        className="entity-list-title"
        data-testid="entity-list-title"
      >
        {entityType}
      </h1>
      <FilterControls
        definition={effectiveDefinition}
        allowlist={allowlist}
        filter={filterState.filter}
        addFilter={filterState.addFilter}
        clearFilter={filterState.clearFilter}
      />
      {isGantt ? (
        // FR-UI-VIEW-3 — gantt view dispatches FIRST in the 4-way
        // dispatch (gantt → tree → kanban → table).
        <GanttView entityType={entityType} definition={effectiveDefinition} />
      ) : isTree ? (
        <TreeView entityType={entityType} definition={effectiveDefinition} />
      ) : isKanban ? (
        <KanbanBoard entityType={entityType} definition={effectiveDefinition} />
      ) : (
        <EntityListTable
          definition={effectiveDefinition}
          page={list.data}
          sort={filterState.sort}
          onToggleSort={handleToggleSort}
        />
      )}
      <PaginationBar
        pageSize={list.data.pageSize}
        hasNextPage={pagination.hasNextPage}
        onNext={handleNext}
      />
    </main>
  );
}

/**
 * ListErrorSurface — renders one of the 4 error states derived
 * from `classifyListError`. Used by both the definition-fetch
 * error path and the records-fetch error path so the surface
 * shape is identical (single source of truth — the security
 * review MAJOR finding is addressed by routing both paths through
 * the same renderer).
 *
 * The component lives in this file (rather than a sibling) because
 * the page and the error surface share the same i18n chrome keys
 * (`lists.title`, `lists.error.fetch_failure`) — colocating them
 * avoids a duplicated key namespace.
 */
function ListErrorSurface({
  status,
  error,
  onRetry,
}: {
  status: EntityListStatus;
  error: ApiError;
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  if (status === "permission-denied") {
    return (
      <main
        data-testid="entity-list-page--permission-denied"
        aria-labelledby="entity-list-title"
        className="entity-list-page entity-list-page--permission-denied"
      >
        <h1
          id="entity-list-title"
          className="entity-list-title visually-hidden"
        >
          {t("lists.title", "Records")}
        </h1>
        <PermissionDenied />
      </main>
    );
  }
  if (status === "stale-version") {
    return (
      <main
        data-testid="entity-list-page--stale-version"
        aria-labelledby="entity-list-title"
        className="entity-list-page entity-list-page--stale-version"
      >
        <h1
          id="entity-list-title"
          className="entity-list-title visually-hidden"
        >
          {t("lists.title", "Records")}
        </h1>
        <StaleVersionError />
      </main>
    );
  }
  if (status === "rate-limit") {
    return (
      <main
        data-testid="entity-list-page--rate-limit"
        aria-labelledby="entity-list-title"
        className="entity-list-page entity-list-page--rate-limit"
      >
        <h1
          id="entity-list-title"
          className="entity-list-title visually-hidden"
        >
          {t("lists.title", "Records")}
        </h1>
        <RateLimitBackpressure retryAfterSeconds={error.retryAfterSeconds} />
      </main>
    );
  }
  // fetch-failure (default)
  return (
    <main
      data-testid="entity-list-page--fetch-failure"
      aria-labelledby="entity-list-title"
      className="entity-list-page entity-list-page--fetch-failure"
    >
      <h1 id="entity-list-title" className="entity-list-title visually-hidden">
        {t("lists.title", "Records")}
      </h1>
      <FetchError
        message={t("lists.error.fetch_failure", "Could not load records.")}
        onRetry={onRetry}
      />
    </main>
  );
}
